import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { onAppEvent } from '../../../lib/events'
import { openFeedbackForm } from '../../../lib/sentry'
import { useWindowContext } from './WindowContext'
import { WindowControls } from './WindowControls'
import { TrafficLights } from './TrafficLights'
import { useTitlebarContext } from './TitlebarContext'
import { Megaphone } from 'lucide-react'
import { SidebarRightIcon } from './icons/SidebarRightIcon'
import { SidebarRightCollapsedIcon } from './icons/SidebarRightCollapsedIcon'
import { SidebarToggle, SearchButton, LogoTitle } from './controls'
import { SyncStatusIcon } from '../../common/SyncStatusIcon'
import './Titlebar.scss'

const SETUP_ROUTES = ['/', '/setup-profile', '/setup-purpose', '/setup-intent']
const ICON_SIZE = 16

/**
 * Titlebar - Plattform-adaptive Titelleiste
 *
 * Rendert automatisch die korrekte Titelleiste basierend auf der Plattform:
 * - **Windows**: Hamburger-Menü + Logo + Title + Search + Window Controls
 * - **macOS**: Traffic Lights + Sidebar Toggle + Search
 * - **Web**: Sidebar Toggle + Search
 *
 * Icons stammen aus Lucide React (Menu, Search, CircleHelp, PanelLeft, PanelRight).
 * Window Controls (Win) und Traffic Lights (Mac) sind eigene Sub-Komponenten.
 *
 * @example
 * ```tsx
 * // Wird automatisch von WindowContextProvider gerendert:
 * <WindowContextProvider titlebar={{ title: 'HappyTask', icon: <SmileyIcon /> }}>
 *   <MyPage />
 * </WindowContextProvider>
 * ```
 */
const TitlebarComponent = () => {
  const { t } = useTranslation()
  const { titlebar, window: windowProps } = useWindowContext()
  const { title, icon, hideTitle, titleCentered, showAppControls: showAppControlsProp, onClose } = titlebar
  const platform = windowProps?.platform // 'win32' | 'darwin' | 'web'
  const isElectron = platform !== 'web'

  // Route-aware: hide app controls on setup pages regardless of windowType.
  // Titlebar is rendered outside <Router>, so we listen for 'route-change' events
  // dispatched by RouteTracker (inside Router) instead of polling.
  const getPath = () => isElectron
    ? (window.location.hash.replace('#', '') || '/')
    : window.location.pathname
  const [currentPath, setCurrentPath] = useState(getPath)

  useEffect(() => onAppEvent('route-change', setCurrentPath), [])

  const isSetupRoute = SETUP_ROUTES.includes(currentPath)

  // On Electron: resize window to 860x500 when navigated to setup route
  useEffect(() => {
    if (isElectron && isSetupRoute && window.api) {
      window.api.invoke('resize-for-setup').catch(() => {})
    }
  }, [isElectron, isSetupRoute])

  const { sidebarToggleRef, rightSidebarToggleRef, sidebarLeftOpen, sidebarRightOpen, mainLayoutActive, registerTabBarSlot } = useTitlebarContext()
  const isMainWindow = isElectron ? (windowProps?.windowType === 'main' || mainLayoutActive) : mainLayoutActive
  const showAppControls = !isSetupRoute && (showAppControlsProp ?? isMainWindow)

  const handleSidebarToggle = useCallback(() => {
    sidebarToggleRef.current?.()
  }, [sidebarToggleRef])

  const handleRightSidebarToggle = useCallback(() => {
    rightSidebarToggleRef.current?.()
  }, [rightSidebarToggleRef])

  const handleTitlebarDoubleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    // Single combined selector instead of 5 separate .closest() calls
    if (target.closest('.titlebar__left-controls, .titlebar__right-controls, .tab-bar')) {
      return
    }
    if (windowProps?.maximizable !== false) {
      window.api?.invoke('window-maximize-toggle').catch(() => {})
    }
  }, [windowProps?.maximizable])

  return (
    <div
      className={`titlebar ${platform ? `titlebar--${platform}` : ''}`}
      onDoubleClick={isElectron ? handleTitlebarDoubleClick : undefined}
    >
      <div className={`titlebar__left-controls ${showAppControls ? (sidebarLeftOpen ? 'titlebar__left-controls--sidebar-open' : 'titlebar__left-controls--sidebar-collapsed') : ''}`}>
        {/* Windows: Hamburger + Logo + Title + Search (only in main window) */}
        {platform === 'win32' && showAppControls && (
          <>
            <SidebarToggle variant="hamburger" isOpen={sidebarLeftOpen} onClick={handleSidebarToggle} label={t('titlebar.toggleSidebar')} />
            <LogoTitle icon={icon} title={title} hideTitle={hideTitle} visible={sidebarLeftOpen} />
            <SearchButton label={t('titlebar.search')} />
          </>
        )}

        {/* macOS: Traffic Lights always visible, Sidebar Icon + Search only in main window */}
        {platform === 'darwin' && (
          <>
            <div className="titlebar__traffic-lights">
              <TrafficLights />
            </div>
            {showAppControls && (
              <>
                <SidebarToggle variant="sidebar-icon" isOpen={sidebarLeftOpen} onClick={handleSidebarToggle} label={t('titlebar.toggleSidebar')} />
                <SearchButton label={t('titlebar.search')} />
              </>
            )}
          </>
        )}

        {/* Web: Sidebar Icon + Logo + Title + Search only in main window */}
        {platform === 'web' && showAppControls && (
          <>
            <SidebarToggle variant="sidebar-icon" isOpen={sidebarLeftOpen} onClick={handleSidebarToggle} label={t('titlebar.toggleSidebar')} />
            <LogoTitle icon={icon} title={title} hideTitle={hideTitle} visible={sidebarLeftOpen} />
            <SearchButton label={t('titlebar.search')} />
          </>
        )}
      </div>

      {/* Centered title — used by standalone windows (Preferences, Setup)
          where `titleCentered: true` is passed via the WindowContextProvider.
          Sits absolutely above the drag area so it doesn't steal flex space
          from left/right controls, and `pointer-events: none` keeps the
          window-drag behaviour intact. */}
      {titleCentered && !hideTitle && title && (
        <div className="titlebar__centered-title" aria-hidden="false">
          {title}
        </div>
      )}

      {/* TabBar slot (portaled from MainLayout) + remaining drag area */}
      {showAppControls ? (
        <div
          ref={registerTabBarSlot}
          className={`titlebar__tab-bar-slot ${isElectron ? 'titlebar__tab-bar-slot--electron' : ''}`}
        />
      ) : (
        <div className={isElectron ? 'titlebar__drag-area' : 'titlebar__spacer'} />
      )}

      {/* Right Controls */}
      <div className="titlebar__right-controls">
        {showAppControls && (
          <>
            <SyncStatusIcon />
            <button
              className="titlebar__help-button"
              onClick={openFeedbackForm}
              aria-label={t('titlebar.feedback')}
            >
              <Megaphone size={ICON_SIZE} />
              <span className="titlebar__help-button-label">{t('titlebar.feedback')}</span>
            </button>
            <button
              className="titlebar__right-sidebar-toggle"
              onClick={handleRightSidebarToggle}
              aria-label={t('titlebar.toggleRightSidebar')}
            >
              {sidebarRightOpen ? <SidebarRightIcon size={ICON_SIZE} /> : <SidebarRightCollapsedIcon size={ICON_SIZE} />}
            </button>
          </>
        )}
        {platform === 'win32' && <WindowControls />}
      </div>
    </div>
  )
}

export const Titlebar = React.memo(TitlebarComponent)
